/**
 * EL FILTRO DE FECHAS — un botón que dice qué estás mirando.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ NO SON DOS CASILLAS DE FECHA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Antes eran dos `<input type="date">` sueltos. Funcionaban, y aun así estaban
 * mal por tres motivos que se notan todos los días:
 *
 *   1. **Casi nadie quiere un rango.** Quiere «esta semana», «el mes pasado»,
 *      «ayer». Con dos casillas hay que calcular las dos fechas a mano cada
 *      vez, y una de cada tres veces se teclea mal el mes.
 *
 *   2. **No dicen qué estás mirando.** «23/08/2026 a 30/08/2026» obliga a
 *      restar mentalmente para saber que son ocho días.
 *
 *   3. **El calendario del navegador es distinto en cada uno**, y en el móvil
 *      abre una rueda de tres columnas para elegir un día.
 *
 * Aquí el 90 % de las veces se resuelve con un toque en un atajo, y el rango a
 * medida sigue estando para el 10 % restante. El botón siempre dice en
 * palabras qué se está mirando.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, Check, ChevronDown } from "lucide-react";
import { Boton, Campo, cx } from "@/components/ui";

export type Rango = { desde: string; hasta: string };

/** Los atajos, en el orden en que se usan de verdad. */
const ATAJOS: { clave: string; nombre: string; calcular: () => Rango }[] = [
  { clave: "hoy", nombre: "Hoy", calcular: () => ({ desde: iso(0), hasta: iso(0) }) },
  { clave: "ayer", nombre: "Ayer", calcular: () => ({ desde: iso(1), hasta: iso(1) }) },
  { clave: "7dias", nombre: "Últimos 7 días", calcular: () => ({ desde: iso(6), hasta: iso(0) }) },
  { clave: "30dias", nombre: "Últimos 30 días", calcular: () => ({ desde: iso(29), hasta: iso(0) }) },
  {
    clave: "mes",
    nombre: "Este mes",
    calcular: () => {
      const h = new Date();
      return { desde: comoIso(new Date(h.getFullYear(), h.getMonth(), 1)), hasta: iso(0) };
    },
  },
  {
    clave: "mes_pasado",
    nombre: "El mes pasado",
    calcular: () => {
      const h = new Date();
      return {
        desde: comoIso(new Date(h.getFullYear(), h.getMonth() - 1, 1)),
        // El día 0 del mes actual es el último del anterior.
        hasta: comoIso(new Date(h.getFullYear(), h.getMonth(), 0)),
      };
    },
  },
];

/** El rango de un atajo, para abrir una pantalla ya en él: `rangoDelAtajo("mes")`. */
export function rangoDelAtajo(
  clave: "hoy" | "ayer" | "7dias" | "30dias" | "mes" | "mes_pasado",
): Rango {
  return ATAJOS.find((a) => a.clave === clave)!.calcular();
}

export function FiltroDeFechas({
  rango,
  onCambiar,
  todas,
}: {
  rango: Rango;
  onCambiar: (r: Rango) => void;
  /**
   * Ofrece «Todas las fechas» arriba del todo, que manda el rango vacío.
   *
   * Es para las listas donde lo normal es NO filtrar —lo que se debe, por
   * ejemplo—: una deuda de hace tres meses no puede quedar escondida porque
   * la pantalla abrió en «este mes».
   */
  todas?: boolean;
}) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement | null>(null);

  // Se cierra al tocar fuera y con Escape. Un desplegable que solo se cierra
  // con su propio botón se queda abierto tapando la tabla.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  /*
    Qué atajo corresponde al rango actual, si es que hay alguno.

    Se calcula comparando fechas y NO guardando cuál se pulsó. Así, si alguien
    escribe a mano un rango que resulta ser «esta semana», el botón lo dice con
    palabras — que es más útil que dos fechas aunque no lo haya elegido del
    menú.
  */
  const atajoActual = useMemo(
    () =>
      ATAJOS.find((a) => {
        const r = a.calcular();
        return r.desde === rango.desde && r.hasta === rango.hasta;
      }),
    [rango],
  );

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className={cx(
          "flex h-11 w-full items-center gap-2 rounded-[var(--radius-control)] border px-3.5 text-sm sm:w-auto",
          "transition-colors",
          abierto ? "border-accent bg-accent-soft text-ink" : "border-line bg-surface text-ink hover:bg-surface-hover",
        )}
      >
        <CalendarDays size={16} className="shrink-0 text-muted" />
        <span className="min-w-0 flex-1 truncate text-left font-medium">
          {atajoActual
            ? atajoActual.nombre
            : !rango.desde && !rango.hasta
              ? "Todas las fechas"
              : enPalabras(rango)}
        </span>
        {/* Los días que abarca, para no tener que restar de cabeza. */}
        <span className="shrink-0 text-xs text-muted">{cuantosDias(rango)}</span>
        <ChevronDown
          size={15}
          className={cx("shrink-0 text-muted transition-transform", abierto && "rotate-180")}
        />
      </button>

      {abierto && (
        <div
          className={cx(
            "absolute left-0 top-full z-30 mt-1.5 w-[min(20rem,calc(100vw-2rem))]",
            "rounded-[var(--radius-card)] border border-line bg-surface p-1.5 shadow-[var(--shadow-raised)]",
          )}
        >
          {todas && (
            <button
              type="button"
              onClick={() => {
                onCambiar({ desde: "", hasta: "" });
                setAbierto(false);
              }}
              className={cx(
                "flex w-full items-center gap-2 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm transition-colors",
                !rango.desde && !rango.hasta
                  ? "bg-accent-soft font-medium text-accent"
                  : "text-ink hover:bg-surface-hover",
              )}
            >
              <Check size={14} className={cx("shrink-0", (rango.desde || rango.hasta) && "opacity-0")} />
              Todas las fechas
            </button>
          )}
          {ATAJOS.map((a) => {
            const elegido = atajoActual?.clave === a.clave;
            return (
              <button
                key={a.clave}
                type="button"
                onClick={() => {
                  onCambiar(a.calcular());
                  setAbierto(false);
                }}
                className={cx(
                  "flex w-full items-center gap-2 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm transition-colors",
                  elegido ? "bg-accent-soft font-medium text-accent" : "text-ink hover:bg-surface-hover",
                )}
              >
                <Check size={14} className={cx("shrink-0", !elegido && "opacity-0")} />
                {a.nombre}
              </button>
            );
          })}

          {/*
            El rango a medida va ABAJO y separado. Es la salida para lo que los
            atajos no cubren —«del 3 al 12 porque fue la feria»— pero ponerlo
            arriba haría que la mayoría tuviera que pasarlo por encima cada vez.
          */}
          <div className="mt-1.5 border-t border-line px-2 pb-1 pt-2.5">
            <p className="rotulo mb-1.5">O elige los días</p>
            <div className="flex items-center gap-1.5">
              <Campo
                type="date"
                value={rango.desde}
                max={rango.hasta}
                onChange={(e) => onCambiar({ ...rango, desde: e.target.value })}
                aria-label="Desde"
                className="h-9 min-w-0 flex-1 px-2 text-sm"
              />
              <span className="shrink-0 text-xs text-muted">a</span>
              <Campo
                type="date"
                value={rango.hasta}
                min={rango.desde}
                onChange={(e) => onCambiar({ ...rango, hasta: e.target.value })}
                aria-label="Hasta"
                className="h-9 min-w-0 flex-1 px-2 text-sm"
              />
            </div>
            <Boton
              variante="secundario"
              tamano="sm"
              className="mt-2 w-full"
              onClick={() => setAbierto(false)}
            >
              Aplicar
            </Boton>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  Fechas
// =============================================================================

/** La fecha de hace `n` días, en formato ISO corto. */
function iso(n: number): string {
  const f = new Date();
  f.setDate(f.getDate() - n);
  return comoIso(f);
}

/**
 * ⚠️ Se arma a mano y NO con `toISOString()`.
 *
 * `toISOString()` pasa a UTC: para alguien en Caracas, a las ocho de la noche
 * del día 5 devuelve el día 6. El filtro se saltaría el día que la persona está
 * mirando, justo a la hora en que se cierra el negocio.
 */
function comoIso(f: Date): string {
  const mes = String(f.getMonth() + 1).padStart(2, "0");
  const dia = String(f.getDate()).padStart(2, "0");
  return `${f.getFullYear()}-${mes}-${dia}`;
}

function enPalabras(r: Rango): string {
  const d = corta(r.desde);
  const h = corta(r.hasta);
  return d === h ? d : `${d} — ${h}`;
}

function corta(iso: string): string {
  if (!iso) return "—";
  return new Date(iso + "T12:00:00").toLocaleDateString("es-VE", {
    day: "numeric",
    month: "short",
  });
}

function cuantosDias(r: Rango): string {
  if (!r.desde || !r.hasta) return "";
  const a = new Date(r.desde + "T12:00:00").getTime();
  const b = new Date(r.hasta + "T12:00:00").getTime();
  const n = Math.round((b - a) / 86_400_000) + 1;
  if (n <= 0) return "";
  return n === 1 ? "1 día" : `${n} días`;
}
